import { ref, computed } from 'vue'
import { apiClient } from '../utils/apiClient.js'

/**
 * Business state (shared across app)
 */
const business = ref(null)
const isLoading = ref(false)
const hasFetched = ref(false)

/**
 * Business composable
 * Provides reactive business state and methods
 * Fetches business info once and caches it
 */
export function useBusiness() {
  const businessName = computed(() => business.value?.name || '')
  const businessId = computed(() => business.value?.id || null)

  /**
   * Fetch business info (only once per session)
   */
  const fetchBusiness = async (force = false) => {
    // Skip if already fetched (unless forced)
    if (hasFetched.value && !force) {
      return business.value
    }

    // Skip if not authenticated
    if (!apiClient.isAuthenticated()) {
      return null
    }

    isLoading.value = true
    try {
      const response = await apiClient.get('/v1/business')
      business.value = response?.business || response
      hasFetched.value = true
      return business.value
    } catch (error) {
      console.warn('Could not fetch business:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear business data (on logout)
   */
  const clearBusiness = () => {
    business.value = null
    hasFetched.value = false
  }

  return {
    // State
    business,
    businessName,
    businessId,
    isLoading,

    // Methods
    fetchBusiness,
    clearBusiness,
  }
}
